import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EVENT_CATS = ["Light Decoration", "Birthday", "Baby Shower", "Home Decoration", "Wedding", "Varmala Entry", "Other"];

export default function GalleryAdmin() {
  const [rows, setRows] = useState([]);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/gallery").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file); fd.append("caption", caption); fd.append("category", category);
      try { await api.post("/admin/gallery", fd); ok += 1; }
      catch { /* continue */ }
    }
    toast[ok ? "success" : "error"](ok ? `Uploaded ${ok} photo${ok > 1 ? "s" : ""}` : "Upload failed");
    setCaption(""); setCategory(""); load();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const remove = async (id) => { await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <h1 className="font-display text-3xl text-zinc-900 mb-6">Gallery</h1>
      <div className="bg-card border border-pink-200 rounded-xl p-5 mb-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <Input placeholder="Caption" value={caption} data-testid="gallery-caption" onChange={(e) => setCaption(e.target.value)} className="bg-white border-pink-200 text-zinc-900" />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="gallery-category" className="bg-white border-pink-200 text-zinc-900"><SelectValue placeholder="Select event" /></SelectTrigger>
          <SelectContent>{EVENT_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <label>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="gallery-file" onChange={(e) => upload(e.target.files)} />
          <Button asChild className="gold-gradient text-white font-semibold cursor-pointer"><span>{uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-1" />} Upload Photos</span></Button>
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {rows.map((g) => (
          <div key={g.id} className="relative group rounded-xl overflow-hidden border border-pink-200" data-testid={`gallery-admin-${g.id}`}>
            <img src={fileUrl(g.storage_path)} alt={g.caption} className="w-full h-40 object-cover" />
            <button onClick={() => remove(g.id)} className="absolute top-2 right-2 bg-black/60 text-red-400 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`del-gallery-${g.id}`}><Trash2 size={15} /></button>
            {g.caption && <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 text-xs text-zinc-900">{g.caption}</div>}
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full py-16 text-center text-zinc-500">No photos uploaded</div>}
      </div>
    </div>
  );
}
